import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useHttpClient } from "shared/hooks/http-hook";

// Reusable API surface for the Tour Builder feature.
// Mirrors the app's other backend hooks (personel-service-hook): thin wrappers around
// useHttpClient that surface errors via toast and return normalized payloads.
const BASE = () => `${process.env.REACT_APP_BACKEND_URL}/tour-builder`;

export const useTourBuilderApi = () => {
  const { sendRequest, isLoading, error } = useHttpClient();
  const { t } = useTranslation();

  const surfaceError = useCallback(
    (err) => {
      // 628 (usage limit) is already toasted inside useHttpClient — don't double-toast.
      if (err?.code === 628) return;
      if (err?.message) {
        window.toast?.error(err.message);
      } else if (err?.code) {
        window.toast?.error(t("httpCodes." + err.code));
      } else {
        window.toast?.error(t("actionFailed"));
      }
    },
    [t],
  );

  const listPlaces = useCallback(async () => {
    try {
      const data = await sendRequest(`${BASE()}/places`, "GET");
      return data?.places || [];
    } catch (err) {
      surfaceError(err);
      return [];
    }
  }, [sendRequest, surfaceError]);

  const createPlace = useCallback(
    async (payload) => {
      try {
        const data = await sendRequest(`${BASE()}/places`, "POST", JSON.stringify(payload));
        return data?.place || null;
      } catch (err) {
        surfaceError(err);
        return null;
      }
    },
    [sendRequest, surfaceError],
  );

  const updatePlace = useCallback(
    async (placeId, payload) => {
      try {
        const data = await sendRequest(`${BASE()}/places/${placeId}`, "PATCH", JSON.stringify(payload));
        return data?.place || null;
      } catch (err) {
        surfaceError(err);
        return null;
      }
    },
    [sendRequest, surfaceError],
  );

  const deletePlace = useCallback(
    async (placeId) => {
      try {
        const data = await sendRequest(`${BASE()}/places/${placeId}`, "DELETE");
        return data || { deleted: false };
      } catch (err) {
        surfaceError(err);
        return null;
      }
    },
    [sendRequest, surfaceError],
  );

  const getTour = useCallback(
    async (tourId) => {
      try {
        const data = await sendRequest(`${BASE()}/${tourId}`, "GET");
        return data?.tour || null;
      } catch (err) {
        surfaceError(err);
        return null;
      }
    },
    [sendRequest, surfaceError],
  );

  const createTour = useCallback(
    async (payload) => {
      try {
        const data = await sendRequest(`${BASE()}`, "POST", JSON.stringify(payload));
        return data?.personelservice || data?.tour || null;
      } catch (err) {
        surfaceError(err);
        return null;
      }
    },
    [sendRequest, surfaceError],
  );

  const updateTour = useCallback(
    async (tourId, payload) => {
      try {
        const data = await sendRequest(`${BASE()}/${tourId}`, "PATCH", JSON.stringify(payload));
        return data?.personelservice || data?.tour || null;
      } catch (err) {
        surfaceError(err);
        return null;
      }
    },
    [sendRequest, surfaceError],
  );

  return {
    listPlaces,
    createPlace,
    updatePlace,
    deletePlace,
    getTour,
    createTour,
    updateTour,
    isLoading,
    error,
  };
};
